import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Dialog,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import type { BusinessesApi } from "../api";
import { createBusinessesApi } from "../api";
import type {
  GigCredentialDecision,
  GigCredentialDto,
  GigCredentialQueueRowDto,
} from "../types";

/**
 * Reviewing the qualifications a gig proves with a document — ADR-004.
 *
 * The queue lists pending credentials only. A reviewer never holds a document
 * URL: each Open asks the API for a fresh two-minute signed link (the API
 * writes an audit row before it signs) and that link goes straight into a new
 * tab. Nothing here caches, stores or renders the URL.
 *
 * The API already drops credentials belonging to a business the reviewer
 * works for. The queue stays filtered so the wrong reviewer is never handed
 * the work; if a relationship is created while the page is open, the attempt
 * is refused and the refusal is shown in plain words rather than as a 403.
 */

/** The API's ceiling is 500; the queue is oldest-first and uncursored. */
const QUEUE_LIMIT = 200;
/** Mirrors CREDENTIAL_REVIEW_NOTE_MIN in the API. */
const NOTE_MIN = 3;
/** Mirrors CREDENTIAL_REVIEW_NOTE_MAX in the API. */
const NOTE_MAX = 500;

type DecisionBusy = "opening" | "deciding" | null;

interface Refusal {
  title: string;
  body: string;
  /** True when the refusal is about this credential, not the reviewer's grants. */
  independence: boolean;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatSize(bytes: number | null): string {
  if (bytes === null || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Turn a 403 into the sentence a reviewer can act on. Independence refusals
 * are the common case here; a missing grant is the other.
 */
function refusalFrom(
  err: unknown,
  action: "view" | "manage",
): Refusal | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const message = apiErrorMessage(err);
  if (/relationship with this business/i.test(message)) {
    return {
      title: "This business is tied to your account",
      body: "You have a relationship with this business, so another reviewer must open and decide its credentials. Nothing was changed.",
      independence: true,
    };
  }
  if (/uploaded this credential/i.test(message)) {
    return {
      title: "You uploaded this credential",
      body: "A credential is reviewed by someone other than the person who uploaded it. Nothing was changed.",
      independence: true,
    };
  }
  return {
    title: "You cannot do this",
    body: `${message} ${
      action === "manage"
        ? "Approving or rejecting needs the verification manage permission on your access assignment."
        : "Opening a credential document needs the verification view permission on your access assignment."
    } Ask an administrator if you expect to have it.`,
    independence: false,
  };
}

export function CredentialReviewPage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);
  const toast = useToast();

  usePageBreadcrumbs([
    { label: "Business Center", to: "/app/businesses" },
    { label: "Credential review" },
  ]);

  const [rows, setRows] = useState<GigCredentialQueueRowDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Set when the API refuses a decision with 403 for lack of
   * `verification:manage`. The table then stays readable but stops offering
   * buttons that always fail.
   */
  const [decideDenied, setDecideDenied] = useState<string | null>(null);
  /** What the last action did, kept on screen after the row leaves the queue. */
  const [outcome, setOutcome] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<GigCredentialQueueRowDto | null>(
    null,
  );

  /**
   * `silent` is a background refresh: it must not swap the table for a
   * spinner while a reviewer is reading it. A silent failure keeps the rows on
   * screen and only warns that they may be stale.
   */
  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      try {
        const res = await api.listCredentialsAwaitingReview(QUEUE_LIMIT);
        setRows(res.credentials ?? []);
        setDenied(false);
        setError(null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setDenied(true);
          setRows(null);
        } else {
          setError(apiErrorMessage(err));
          if (!opts?.silent) setRows(null);
        }
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // New uploads finish while the queue is open; the reviewer should see them
  // land without being told to refresh.
  useLiveRefresh(() => void load({ silent: true }));

  const count = rows?.length ?? 0;

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Credential review"
        description="Qualifications gigs submit for the verified badge. Each Open is logged and issues a link that expires in two minutes. You never see a credential from a business you work for, or one you uploaded."
        actions={
          <Flex gap={8}>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses")}
            >
              Business Center
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      {outcome ? (
        <Alert tone="success" title="Decision recorded">
          {outcome}
        </Alert>
      ) : null}

      {decideDenied ? (
        <Alert tone="info" title="Read-only for you">
          {decideDenied} You can still open the document; approving or rejecting
          needs another reviewer.
        </Alert>
      ) : null}

      {error && rows !== null ? (
        <Alert tone="warning" title="The queue may be out of date">
          {error}
        </Alert>
      ) : null}

      {denied ? (
        <EmptyState
          reason="scope"
          title="You cannot see the credential review queue"
          description="It needs the verification view permission on your access assignment. Ask an administrator to grant it."
        />
      ) : loading && rows === null ? (
        <LoadingState message="Loading the credential review queue…" />
      ) : error && rows === null ? (
        <Alert tone="danger" title="Unable to load the credential review queue">
          <Flex gap={8} align="center" wrap>
            <Text role="small" as="span">
              {error}
            </Text>
            <Button size="sm" variant="secondary" onClick={() => void load()}>
              Retry
            </Button>
          </Flex>
        </Alert>
      ) : rows !== null && count === 0 ? (
        <EmptyState
          reason="complete"
          title="No credentials awaiting review"
          description="Every submitted credential has been approved or rejected, or none has been submitted yet. A gig uploads one from its credential page in the HUDDL app; it appears here as soon as the upload is checked. Credentials from businesses tied to your account are excluded and handled by another reviewer."
          action={
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses")}
            >
              Back to Business Center
            </Button>
          }
        />
      ) : rows !== null ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                {count} credential{count === 1 ? "" : "s"} awaiting review, oldest
                first
              </Text>
            }
            right={
              count >= QUEUE_LIMIT ? (
                <Text role="small" color="secondary" as="span">
                  Showing the {QUEUE_LIMIT} oldest. Decide some to see the rest.
                </Text>
              ) : undefined
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Business</Th>
                <Th>Credential</Th>
                <Th>Issuer</Th>
                <Th>Skill</Th>
                <Th>Submitted</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <Text role="bodyStrong" as="div">
                      {row.business_name}
                    </Text>
                  </Td>
                  <Td>
                    <Stack gap={4}>
                      <Text role="body" as="span">
                        {row.title}
                      </Text>
                      {!row.file_available ? (
                        <Text role="caption" color="muted" as="span">
                          No readable file — cannot be reviewed
                        </Text>
                      ) : null}
                    </Stack>
                  </Td>
                  <Td>{row.issuer ?? "—"}</Td>
                  <Td>
                    {row.skill_name ?? (
                      <Text role="small" color="muted" as="span">
                        All services
                      </Text>
                    )}
                  </Td>
                  <Td>{formatWhen(row.created_at)}</Td>
                  <Td>
                    <StatusChip tone="warning">Pending</StatusChip>
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setReviewing(row)}
                    >
                      Review
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <CredentialReviewDialog
        api={api}
        row={reviewing}
        decideDenied={decideDenied}
        onClose={() => setReviewing(null)}
        onRefreshQueue={() => {
          setReviewing(null);
          void load({ silent: true });
        }}
        onDecideDenied={setDecideDenied}
        onDecided={(input) => {
          setReviewing(null);
          const message =
            input.decision === "approve"
              ? `Approved “${input.title}” for ${input.businessName}. The gig now shows it as a verified credential.`
              : `Rejected “${input.title}” for ${input.businessName}. The gig reads your note in the HUDDL app.`;
          setOutcome(message);
          toast.push({
            title:
              input.decision === "approve"
                ? "Credential approved"
                : "Credential rejected",
            description: `${input.title} · ${input.businessName}`,
            tone: input.decision === "approve" ? "success" : "warning",
          });
          void load({ silent: true });
        }}
      />
    </Stack>
  );
}

/* ── The review surface ───────────────────────────────────────────────── */

type ReviewMode = "details" | "approve" | "reject";

function CredentialReviewDialog({
  api,
  row,
  decideDenied,
  onClose,
  onRefreshQueue,
  onDecideDenied,
  onDecided,
}: {
  api: BusinessesApi;
  /** Null while closed. */
  row: GigCredentialQueueRowDto | null;
  decideDenied: string | null;
  onClose: () => void;
  onRefreshQueue: () => void;
  onDecideDenied: (message: string) => void;
  onDecided: (input: {
    decision: GigCredentialDecision;
    title: string;
    businessName: string;
  }) => void;
}) {
  const [detail, setDetail] = useState<GigCredentialDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailRefusal, setDetailRefusal] = useState<Refusal | null>(null);
  const [mode, setMode] = useState<ReviewMode>("details");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<DecisionBusy>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [openNotice, setOpenNotice] = useState<string | null>(null);

  // Opening a row loads its full metadata (reference, file name, size, any
  // prior verdict). The queue row carries only what a list needs.
  useEffect(() => {
    setDetail(null);
    setDetailError(null);
    setDetailRefusal(null);
    setMode("details");
    setNote("");
    setActionError(null);
    setRefusal(null);
    setOpenNotice(null);
    if (!row) {
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    void (async () => {
      try {
        const res = await api.listBusinessCredentialsForReview(row.business_id);
        if (cancelled) return;
        setDetail((res.credentials ?? []).find((c) => c.id === row.id) ?? null);
      } catch (err) {
        if (cancelled) return;
        const deniedRefusal = refusalFrom(err, "view");
        if (deniedRefusal) setDetailRefusal(deniedRefusal);
        else setDetailError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, row]);

  // The API mints a fresh URL per click. It is put straight into a tab and
  // dropped: no state holds it, so it cannot outlive the click.
  async function openDocument() {
    if (!row || !detail) return;
    setActionError(null);
    setRefusal(null);
    setOpenNotice(null);
    // Open the tab inside the click, before the request, so a pop-up blocker
    // sees a user gesture. The blank tab is navigated once the URL arrives.
    const tab = window.open("", "_blank");
    if (tab) {
      try {
        tab.opener = null;
      } catch {
        // A same-origin about:blank tab always allows this; ignore if not.
      }
    }
    setBusy("opening");
    try {
      const res = await api.openCredentialForReview(row.business_id, detail.id);
      if (tab && !tab.closed) tab.location.href = res.access.url;
      else
        setOpenNotice(
          "Your browser blocked the new tab. Allow pop-ups for HQ, then press Open document again.",
        );
    } catch (err) {
      tab?.close();
      const refused = refusalFrom(err, "view");
      if (refused) setRefusal(refused);
      else setActionError(apiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function decide(decision: GigCredentialDecision) {
    if (!row || !detail) return;
    const trimmed = note.trim();
    if (decision === "reject" && trimmed.length < NOTE_MIN) return;
    setBusy("deciding");
    setActionError(null);
    setRefusal(null);
    try {
      await api.decideCredential(
        row.business_id,
        detail.id,
        decision,
        trimmed || undefined,
      );
      onDecided({
        decision,
        title: detail.title,
        businessName: row.business_name,
      });
    } catch (err) {
      const refused = refusalFrom(err, "manage");
      if (refused) {
        setRefusal(refused);
        if (!refused.independence) {
          // Without verification:manage no credential can be decided. Drop
          // back to the details view, where the actions then disappear.
          onDecideDenied(refused.body);
          setMode("details");
        }
      } else {
        setActionError(apiErrorMessage(err));
      }
    } finally {
      setBusy(null);
    }
  }

  const title = detail?.title ?? row?.title ?? "Credential";
  const pending = detail?.status === "pending";
  const length = note.trim().length;
  const noteOk = length >= NOTE_MIN;
  const working = busy !== null;

  let body: ReactNode;
  if (!row) {
    body = null;
  } else if (detailLoading) {
    body = <LoadingState message="Loading the credential…" />;
  } else if (detailRefusal) {
    body = (
      <Alert tone="warning" title={detailRefusal.title}>
        {detailRefusal.body}
      </Alert>
    );
  } else if (detailError) {
    body = (
      <Alert tone="danger" title="Could not load the credential">
        <Stack gap={8}>
          <Text role="small" as="p">
            {detailError}
          </Text>
          <Button size="sm" variant="secondary" onClick={onRefreshQueue}>
            Refresh the queue
          </Button>
        </Stack>
      </Alert>
    );
  } else if (!detail) {
    body = (
      <Stack gap={12}>
        <Alert tone="info" title="This credential is not on the list">
          It may have been removed by the gig while the queue was open.
        </Alert>
        <Button size="sm" variant="secondary" onClick={onRefreshQueue}>
          Refresh the queue
        </Button>
      </Stack>
    );
  } else if (mode === "approve") {
    body = (
      <Stack gap={12}>
        <Text role="body" as="p">
          Approving <strong>{detail.title}</strong> for {row.business_name} puts
          a “Verified credential” badge on the gig’s public page.
        </Text>
        <Textarea
          label="Note (optional, kept in the audit record)"
          value={note}
          maxLength={NOTE_MAX}
          onChange={(event) => setNote(event.target.value)}
        />
        <Text role="caption" color="muted" as="p">
          The gig does not see this note. A rejection’s note is the one they
          read.
        </Text>
        {refusal ? (
          <Alert tone="warning" title={refusal.title}>
            {refusal.body}
          </Alert>
        ) : null}
        {actionError ? (
          <Alert tone="danger" title="The approval was not saved">
            <Stack gap={8}>
              <Text role="small" as="p">
                {actionError}
              </Text>
              <Button size="sm" variant="secondary" onClick={onRefreshQueue}>
                Refresh the queue
              </Button>
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    );
  } else if (mode === "reject") {
    body = (
      <Stack gap={12}>
        <Text role="small" color="secondary" as="p">
          The gig reads this note in the HUDDL app next to the credential. Say
          what is wrong with the document — unreadable, expired, wrong name, not
          a qualification for the skill — so they know what to replace.
        </Text>
        <Textarea
          label="Reason"
          value={note}
          maxLength={NOTE_MAX}
          onChange={(event) => setNote(event.target.value)}
        />
        <Text role="caption" color="muted" as="p">
          {noteOk
            ? `${length}/${NOTE_MAX}`
            : `At least ${NOTE_MIN} characters, so the gig knows what to fix${
                length > 0 ? ` (${NOTE_MIN - length} more)` : ""
              }.`}
        </Text>
        {refusal ? (
          <Alert tone="warning" title={refusal.title}>
            {refusal.body}
          </Alert>
        ) : null}
        {actionError ? (
          <Alert tone="danger" title="The rejection was not saved">
            <Stack gap={8}>
              <Text role="small" as="p">
                {actionError}
              </Text>
              <Button size="sm" variant="secondary" onClick={onRefreshQueue}>
                Refresh the queue
              </Button>
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    );
  } else {
    body = (
      <Stack gap={16}>
        <Stack gap={4}>
          <Text role="title" as="h3">
            {detail.title}
          </Text>
          <Text role="small" color="secondary" as="p">
            {row.business_name}
          </Text>
        </Stack>

        <Table>
          <Thead>
            <Tr>
              <Th>Field</Th>
              <Th>On the document</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Issuer</Td>
              <Td>{detail.issuer ?? "—"}</Td>
            </Tr>
            <Tr>
              <Td>Skill</Td>
              <Td>
                {row.skill_name ?? (
                  <Text role="small" color="muted" as="span">
                    All services
                  </Text>
                )}
              </Td>
            </Tr>
            <Tr>
              <Td>Reference</Td>
              <Td>{detail.reference ?? "—"}</Td>
            </Tr>
            <Tr>
              <Td>Submitted</Td>
              <Td>{formatWhen(detail.created_at)}</Td>
            </Tr>
            <Tr>
              <Td>File</Td>
              <Td>
                {detail.file_name ?? "—"} · {formatSize(detail.size_bytes)}
              </Td>
            </Tr>
            <Tr>
              <Td>Status</Td>
              <Td>
                <StatusChip
                  tone={
                    detail.status === "approved"
                      ? "success"
                      : detail.status === "rejected"
                        ? "danger"
                        : "warning"
                  }
                >
                  {detail.status === "pending"
                    ? "Pending"
                    : detail.status === "approved"
                      ? "Approved"
                      : "Rejected"}
                </StatusChip>
                {detail.status === "rejected" && detail.review_note ? (
                  <Text role="caption" color="muted" as="div">
                    Previous reason: {detail.review_note}
                  </Text>
                ) : null}
              </Td>
            </Tr>
          </Tbody>
        </Table>

        {!pending ? (
          <Alert tone="info" title="This credential is no longer awaiting review">
            It is {detail.status} now, so it is not in the queue. Refresh to see
            what remains.
          </Alert>
        ) : null}

        {!detail.file_available ? (
          <Alert tone="warning" title="There is no readable file">
            The stored copy is gone, so this credential cannot be opened,
            approved or rejected. Do not try to rule on it; report it to the
            team that owns credential storage.
          </Alert>
        ) : null}

        {decideDenied && pending ? (
          <Alert tone="info" title="Read-only for you">
            {decideDenied} You can still open the document; approving or
            rejecting needs another reviewer.
          </Alert>
        ) : null}

        {refusal ? (
          <Alert tone="warning" title={refusal.title}>
            {refusal.body}
          </Alert>
        ) : null}

        {openNotice ? (
          <Alert tone="warning" title="The document did not open">
            {openNotice}
          </Alert>
        ) : null}

        {actionError ? (
          <Alert tone="danger" title="Something went wrong">
            <Stack gap={8}>
              <Text role="small" as="p">
                {actionError}
              </Text>
              <Button size="sm" variant="secondary" onClick={onRefreshQueue}>
                Refresh the queue
              </Button>
            </Stack>
          </Alert>
        ) : null}

        <Text role="caption" color="muted" as="p">
          Opening mints a fresh link that expires in two minutes and writes an
          audit record. The link is opened in a new tab and never shown here.
        </Text>
      </Stack>
    );
  }

  const footer =
    !row || detailLoading || detailRefusal || detailError || !detail ? (
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    ) : mode === "approve" ? (
      <>
        <Button
          variant="secondary"
          disabled={working}
          onClick={() => {
            setMode("details");
            setActionError(null);
            setRefusal(null);
          }}
        >
          Back
        </Button>
        <Button
          variant="primary"
          loading={busy === "deciding"}
          disabled={working || !pending}
          onClick={() => void decide("approve")}
        >
          Approve credential
        </Button>
      </>
    ) : mode === "reject" ? (
      <>
        <Button
          variant="secondary"
          disabled={working}
          onClick={() => {
            setMode("details");
            setActionError(null);
            setRefusal(null);
          }}
        >
          Back
        </Button>
        <Button
          variant="danger"
          loading={busy === "deciding"}
          disabled={working || !pending || !noteOk}
          onClick={() => void decide("reject")}
        >
          Reject credential
        </Button>
      </>
    ) : (
      <>
        <Button variant="secondary" disabled={working} onClick={onClose}>
          Close
        </Button>
        <Button
          variant="secondary"
          loading={busy === "opening"}
          disabled={working || !detail.file_available}
          title={
            detail.file_available
              ? undefined
              : "There is no readable file to open"
          }
          onClick={() => void openDocument()}
        >
          Open document
        </Button>
        {pending && !decideDenied ? (
          <>
            <Button
              variant="danger"
              disabled={working || !detail.file_available}
              title={
                detail.file_available
                  ? undefined
                  : "There is no readable file to review"
              }
              onClick={() => {
                setNote("");
                setMode("reject");
              }}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              disabled={working || !detail.file_available}
              title={
                detail.file_available
                  ? undefined
                  : "There is no readable file to review"
              }
              onClick={() => {
                setNote("");
                setMode("approve");
              }}
            >
              Approve
            </Button>
          </>
        ) : null}
      </>
    );

  return (
    <Dialog
      open={row !== null}
      // A decision already in flight cannot be called back by closing the
      // dialog; ignore Close until it has answered.
      onClose={() => {
        if (!working) onClose();
      }}
      title={title}
      actions={footer}
    >
      {body}
    </Dialog>
  );
}
